import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Building2, Plus, Edit3, Trash2, X, Star, Lock, Sparkles, Tv as TvIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { usePlanFeature } from '@/hooks/usePlanFeature';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/utils/cn';

interface StoreRow {
  id: number;
  code: string;
  name: string;
  short_name: string | null;
  cnpj: string | null;
  phone: string | null;
  timezone: string | null;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  tvs_count: number;
  created_at: string;
}

export function StoresPage() {
  const { isMasterAdmin } = useAuthStore();
  const { enabled: multiStore, isLoading: featLoading } = usePlanFeature('multi_store');
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<StoreRow | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StoreRow | null>(null);

  const { data: stores = [], isLoading } = useQuery<StoreRow[]>({
    queryKey: ['stores'],
    queryFn: () => apiClient.getStores().then(r => r.data),
  });

  const setDefaultMut = useMutation({
    mutationFn: (id: number) => apiClient.setDefaultStore(id),
    onSuccess: () => {
      toast.success('Loja default atualizada');
      qc.invalidateQueries({ queryKey: ['stores'] });
    },
    onError: () => toast.error('Falha ao definir loja default'),
  });

  const canCreate = stores.length === 0 || multiStore || isMasterAdmin;

  return (
    <div className="container-app py-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 className="h-7 w-7" />Lojas
          </h1>
          <p className="text-muted-foreground mt-1">
            Cadastre suas filiais para organizar TVs, playlists e produtos por loja.
          </p>
        </div>
        {canCreate ? (
          <Button variant="gradient" onClick={() => setOpenCreate(true)}>
            <Plus className="h-4 w-4" />Nova loja
          </Button>
        ) : (
          <Button variant="outline" onClick={() => setUpgradeOpen(true)}>
            <Lock className="h-4 w-4" />Multi-loja
          </Button>
        )}
      </div>

      {!multiStore && !featLoading && stores.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-6 flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-amber-900">Multi-loja disponível no plano Enterprise</div>
            <div className="text-sm text-amber-800 mt-1">
              Você pode editar a loja atual. Para cadastrar filiais e organizar TVs por loja, faça upgrade.
            </div>
          </div>
          <Button size="sm" variant="outline" asChild>
            <Link to="/billing">Ver planos</Link>
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando…</div>
      ) : stores.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground" />
          <h3 className="mt-4 font-semibold">Nenhuma loja cadastrada</h3>
          <p className="text-sm text-muted-foreground mt-1">Cadastre sua matriz para começar.</p>
          <Button className="mt-4" onClick={() => setOpenCreate(true)}>Cadastrar primeira loja</Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {stores.map(s => (
            <div
              key={s.id}
              className={cn(
                'flex items-center gap-4 rounded-lg border bg-card p-4',
                !s.is_active && 'opacity-60'
              )}
            >
              <div className="grid place-items-center h-10 w-10 rounded-md bg-brand-100 text-brand-700 shrink-0">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{s.name}</span>
                  <span className="text-xs text-muted-foreground">#{s.code}</span>
                  {s.is_default && (
                    <Badge variant="success" className="text-[10px]">
                      <Star className="h-3 w-3" />Default
                    </Badge>
                  )}
                  {!s.is_active && <Badge variant="warning" className="text-[10px]">Inativa</Badge>}
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><TvIcon className="h-3 w-3" />{s.tvs_count} TVs</span>
                  {s.cnpj && <span>CNPJ {s.cnpj}</span>}
                  {s.phone && <span>{s.phone}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!s.is_default && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDefaultMut.mutate(s.id)}
                    title="Definir como loja default"
                  >
                    <Star className="h-4 w-4" />
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setEditTarget(s)}>
                  <Edit3 className="h-4 w-4" />
                </Button>
                {!s.is_default && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-rose-600"
                    onClick={() => setDeleteTarget(s)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {openCreate && <StoreFormModal onClose={() => setOpenCreate(false)} onUpgradeNeeded={() => { setOpenCreate(false); setUpgradeOpen(true); }} />}
      {editTarget && <StoreFormModal store={editTarget} onClose={() => setEditTarget(null)} />}
      {upgradeOpen && <UpgradeModal onClose={() => setUpgradeOpen(false)} />}
      {deleteTarget && <DeleteStoreModal store={deleteTarget} allStores={stores} onClose={() => setDeleteTarget(null)} />}
    </div>
  );
}

function StoreFormModal({
  store,
  onClose,
  onUpgradeNeeded,
}: {
  store?: StoreRow;
  onClose: () => void;
  onUpgradeNeeded?: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!store;
  const [form, setForm] = useState({
    code: store?.code ?? '',
    name: store?.name ?? '',
    short_name: store?.short_name ?? '',
    cnpj: store?.cnpj ?? '',
    phone: store?.phone ?? '',
    timezone: store?.timezone ?? '',
    is_active: store?.is_active ?? true,
    sort_order: store?.sort_order ?? 0,
  });

  const mut = useMutation({
    mutationFn: () => {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        short_name: form.short_name.trim() || null,
        cnpj: form.cnpj.trim() || null,
        phone: form.phone.trim() || null,
        timezone: form.timezone.trim() || null,
        is_active: form.is_active,
        sort_order: form.sort_order,
      };
      return isEdit
        ? apiClient.updateStore(store!.id, payload)
        : apiClient.createStore(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Loja atualizada' : 'Loja cadastrada');
      qc.invalidateQueries({ queryKey: ['stores'] });
      onClose();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'feature_not_available') {
        if (onUpgradeNeeded) onUpgradeNeeded();
        else toast.error('Recurso disponível apenas no plano Enterprise');
      } else if (code === 'code_ja_existe') {
        toast.error('Já existe uma loja com esse código');
      } else if (code === 'code_obrigatorio') {
        toast.error('Informe o código da loja');
      } else if (code === 'name_obrigatorio') {
        toast.error('Informe o nome da loja');
      } else {
        toast.error('Falha ao salvar');
      }
    },
  });

  const valid = form.code.trim().length > 0 && form.name.trim().length >= 2;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Building2 className="h-5 w-5" />{isEdit ? 'Editar loja' : 'Nova loja'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1">Código *</label>
              <Input
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
                placeholder="001"
              />
              <p className="text-xs text-muted-foreground mt-1">Igual ao código no ERP</p>
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium block mb-1">Nome *</label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Matriz Centro"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1">Nome curto</label>
              <Input value={form.short_name} onChange={e => setForm({ ...form, short_name: e.target.value })} placeholder="Matriz" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">CNPJ</label>
              <Input value={form.cnpj} onChange={e => setForm({ ...form, cnpj: e.target.value })} placeholder="opcional" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1">Telefone</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(11) 99999-0000" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Timezone</label>
              <Input value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })} placeholder="America/Sao_Paulo" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
            Loja ativa
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-muted/20">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!valid} loading={mut.isPending} onClick={() => mut.mutate()}>
            {isEdit ? 'Salvar' : 'Cadastrar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DeleteStoreModal({
  store,
  allStores,
  onClose,
}: {
  store: StoreRow;
  allStores: StoreRow[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const others = allStores.filter(s => s.id !== store.id);
  const [reassign, setReassign] = useState<number | ''>(others[0]?.id ?? '');

  const mut = useMutation({
    mutationFn: () => apiClient.deleteStore(store.id, reassign ? Number(reassign) : undefined),
    onSuccess: () => {
      toast.success('Loja excluída');
      qc.invalidateQueries({ queryKey: ['stores'] });
      qc.invalidateQueries({ queryKey: ['tvs'] });
      onClose();
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'default_store') toast.error('Promova outra loja a default antes de excluir esta');
      else if (code === 'loja_em_uso') toast.error('Loja tem TVs vinculadas — escolha uma loja de destino');
      else toast.error('Falha ao excluir');
    },
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-rose-600" />Excluir loja
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm">
            Excluir <strong>{store.name}</strong>?
          </p>
          {store.tvs_count > 0 && (
            <div className="rounded-md border bg-amber-50 border-amber-200 p-3 text-sm">
              <p className="text-amber-900">
                Esta loja tem <strong>{store.tvs_count} TV(s)</strong>. Escolha pra qual loja transferir:
              </p>
              <select
                value={reassign}
                onChange={e => setReassign(e.target.value ? Number(e.target.value) : '')}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {others.map(o => (
                  <option key={o.id} value={o.id}>{o.name} (#{o.code})</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-muted/20">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            variant="destructive"
            loading={mut.isPending}
            disabled={store.tvs_count > 0 && !reassign}
            onClick={() => mut.mutate()}
          >
            Excluir
          </Button>
        </div>
      </div>
    </div>
  );
}

function UpgradeModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />Multi-loja
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-3 text-sm">
          <p>
            O plano atual permite apenas <strong>1 loja</strong>. Para cadastrar filiais e organizar
            TVs/produtos por loja, faça upgrade para o plano <strong>Enterprise</strong>.
          </p>
          <ul className="space-y-1 text-muted-foreground text-xs list-disc pl-4">
            <li>Cadastro ilimitado de filiais</li>
            <li>Filtro por loja em TVs e no Calendário de uso</li>
            <li>Preços e promoções por filial (em breve)</li>
          </ul>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-muted/20">
          <Button variant="outline" onClick={onClose}>Agora não</Button>
          <Button asChild variant="gradient">
            <Link to="/billing">Ver planos</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
